import { useState } from "react";
import { Download, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Agent } from "@/types/agent/agent";
import { BrandIntegrityService } from "@/services/agent/BrandIntegrityService";
import {
  BrandIntegrityExcelExport,
  BrandIntegrityExportOptions,
} from "@/services/export/BrandIntegrityExcelExport";

interface ExportOptionsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent;
  dateRange: string;
}

export const ExportOptionsSheet = ({
  isOpen,
  onClose,
  agent,
  dateRange,
}: ExportOptionsSheetProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const [options, setOptions] = useState<BrandIntegrityExportOptions>({
    includeTranscripts: true,
    includeCriteriaDetails: true,
  });

  const handleExport = async () => {
    setIsExporting(true);

    try {
      // Fetch all test runs for the agent
      const testRuns = await BrandIntegrityService.getAllTestRunsForAgent(
        agent.id,
        dateRange
      );

      if (testRuns.length === 0) {
        toast.error("No test data to export");
        setIsExporting(false);
        return;
      }

      // Generate the Excel file
      const exporter = new BrandIntegrityExcelExport(
        agent,
        testRuns,
        options
      );

      const blob = await exporter.generate();

      // Download the file
      BrandIntegrityExcelExport.downloadExcel(blob, agent.name);

      toast.success("Export completed successfully");
      onClose();
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const getDateRangeLabel = () => {
    if (dateRange === "all") return "All time";
    if (dateRange === "7d") return "Last 7 days";
    if (dateRange === "30d") return "Last 30 days";
    if (dateRange === "90d") return "Last 90 days";
    return dateRange;
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-xl flex flex-col h-full p-0"
      >
        <VisuallyHidden asChild>
          <h2>Export Genie Trust Center Data</h2>
        </VisuallyHidden>
        <VisuallyHidden asChild>
          <p>Download a comprehensive Excel report</p>
        </VisuallyHidden>

        {/* Header */}
        <div className="flex items-center justify-between border-b p-6 sm:p-8">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Export Genie Trust Center Data</h2>
            <p className="text-sm text-gray-500 mt-1">
              Download a comprehensive Excel report
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-light transition-colors"
          >
            &times;
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-6">
          {/* Export Summary Card */}
          <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="rounded-[10px] bg-white p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <FileSpreadsheet className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{agent.name}</p>
                  <p className="text-sm text-gray-500">{getDateRangeLabel()}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Your export will include test runs, detailed results, conversation transcripts,
                issues analysis, and evaluation criteria breakdown.
              </p>
            </div>
          </div>

          {/* Export Options */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-4 block">
              Export Options
            </label>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="includeTranscripts"
                  checked={options.includeTranscripts}
                  onCheckedChange={(checked) =>
                    setOptions((prev) => ({ ...prev, includeTranscripts: !!checked }))
                  }
                />
                <div>
                  <label
                    htmlFor="includeTranscripts"
                    className="text-sm font-medium text-gray-900 cursor-pointer"
                  >
                    Include Conversation Transcripts
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Full turn-by-turn conversation logs for each test
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="includeCriteriaDetails"
                  checked={options.includeCriteriaDetails}
                  onCheckedChange={(checked) =>
                    setOptions((prev) => ({ ...prev, includeCriteriaDetails: !!checked }))
                  }
                />
                <div>
                  <label
                    htmlFor="includeCriteriaDetails"
                    className="text-sm font-medium text-gray-900 cursor-pointer"
                  >
                    Include Criteria Breakdown
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Detailed pass/fail results for each evaluation criterion with reasoning
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What's Included Section */}
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-3 block">
              Report Contents
            </label>
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <ReportSheet
                title="Executive Summary"
                description="KPIs, pass rates, scores, and category performance breakdown"
              />
              <ReportSheet
                title="Test Runs"
                description="Overview of all test executions with status and duration"
              />
              <ReportSheet
                title="Detailed Results"
                description="Individual test results with scores, summaries, and call status"
              />
              {options.includeTranscripts && (
                <ReportSheet
                  title="Conversation Transcripts"
                  description="Complete message-by-message conversation logs"
                />
              )}
              <ReportSheet
                title="Issues Analysis"
                description="All identified issues with severity levels and descriptions"
              />
              {options.includeCriteriaDetails && (
                <ReportSheet
                  title="Criteria Breakdown"
                  description="Evaluation criteria results with pass/fail reasoning"
                />
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t px-6 sm:px-8 py-4 flex justify-start gap-3 bg-white">
          <Button variant="outline" onClick={onClose} disabled={isExporting}>
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting}
            className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg"
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export Excel
              </>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const ReportSheet = ({ title, description }: { title: string; description: string }) => (
  <div className="flex items-start gap-3">
    <div className="h-5 w-5 rounded bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
      <div className="h-2 w-2 rounded-sm bg-primary" />
    </div>
    <div>
      <p className="text-sm font-medium text-gray-900">{title}</p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  </div>
);
